/* eslint-disable camelcase */
/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import VideoPlayerComponent from '@rmm/video-player/src/player';
import { trackers } from '@rmm/error-tracker';
import videojs from 'video.js';
import PropTypes from 'prop-types';

import 'video.js/dist/video-js.css';
import 'videojs-ima/dist/videojs.ima.css';
import 'swiper/swiper-bundle.min.css';

import { getCookie } from '@rmm/video-player/src/util/helper';
// eslint-disable-next-line max-len
import {
  applyVideoClassificationToAdTagUrl,
  applyVideoClassificationToFreewheelAdTagUrl,
  customOvpEvent,
  isLiveStream,
  prepareAdTagUrl,
  prepareBids,
  prepareCustParams,
  prepareFreewheelParams,
  refreshBids,
  shouldDisableComscoreBasedOnParams,
  isVideoHero,
} from '../utils/ovpUtils';
import triggerIfVisible from '../utils/triggerIfVisible';

import {
  updateFreewheelAdTagUrlWithBids,
  updateFreewheelUrl,
  addLivestreamFreewheelParams,
} from '../utils/freewheelUtils';
import ppid from '../ads/gam/ppid';

const { SimpleErrorTracker } = trackers;

/**
 * OVP wrapper component
 */
const VideoPlayer = (props) => {
  /**
   * Sticky comes from data-sticky and video_params comes from data-video_params.
   * video_params is transformed into playerParams within ovpRenderer using a function in ovpUtils.js
   * They are set in video-playlist-factory.js inside setCurrentVideo() for OVP player,
   * and inside render-shortcode in class-anvato-player-dependent-feature-definition.php
   * for Anvato/Akta player.
   */
  const {
    allow_render: forceRender,
    sticky,
    minimized,
    bob_pixel_url: bobBaseHost,
    playerParams,
    post_id: postId,
    is_lead_media: isLeadMedia,
    is_post: isPost,
    tracker,
    player_id,
    playerName,
    injected_via: injectedVia,
    video_classifications,
  } = props;

  // TRACKER: Send success for replacing Suspense
  tracker.sendSuccess();

  const trackParams = {
    id: player_id,
    position: playerName,
    renderGroup: 'WP Intermediate component setup',
  };

  const isComscoreEnabled = ! (shouldDisableComscoreBasedOnParams() || ('true' === window.nxstVideoDeliveryDisableComscore)) ;
  const initiallySticky = (parseInt(sticky, 10) || 'true' === sticky);
  const minimizeEnabled = (parseInt(minimized, 10) || 'true' === minimized);
  const [renderPlayer, setRenderPlayer] = useState(false);
  const [ads, setAds] = useState({});
  const [shareEmbed, setShareEmbed] = useState('');

  const {
    autoplay, title, recommendations, isPermutiveEnabled, expectPrerollTimeout, accessKey: anvack, plugins: { nielsen },
    isAmp,
  } = playerParams || {};

  // Hard overwrite for category pages since there is no checkbox on them for allowing title on floating player
  const titleVisible = playerParams?.titleVisible || 'category' === window.NXSTdata?.content?.pageType;
  const ad_tag_url = playerParams.plugins?.dfp?.adTagUrl;
  const extraParams = useRef('');
  const isFreewheelAds = playerParams.plugins?.dfp?.isFreewheel;
  const videoId = playerParams.video || playerParams.playlist;
  const s2nPlayer = useRef();
  const isEmbedVideoInPost = parseInt(isPost, 10) && ! parseInt(isLeadMedia, 10) && !isVideoHero(videoId);
  const slotID = parseInt(isLeadMedia, 10) || initiallySticky ? 'preroll' : 'instreamVideo';
  const isNewsNationTheme = 'newsnation' === window.NXSTdata.site.activeTheme;
  const videoClassifications = video_classifications?.length ? video_classifications : playerParams?.videoClassification;

  const updateShareEmbed = (newVideoId) => {
    const allowShareEmbed = playerParams.htmlEmbed && postId;
    const isValidVideoId = newVideoId && ! isLiveStream(newVideoId);
    if (allowShareEmbed && isValidVideoId) {
      const shareParams = {
        post_id: postId,
        adtag_url: playerParams.plugins?.dfp?.adTagUrl,
        ad_provider: playerParams.plugins?.dfp?.isFreewheel ? 'freewheel' : 'gam',
        injected_via: injectedVia,
        video_id: newVideoId,
      };
      const encodedParams = btoa(JSON.stringify(shareParams));
      setShareEmbed(`/nxs-video-player-embed/${encodedParams}`);
    }
  };

  const updateAdTagWithCompanionAd = (adTagUrl) => {
    const companionIsEnable = window.companionAdData?.isEnabled || false;

    // Add companion ad for TheHill video center
    if (companionIsEnable) {
      return `${adTagUrl}&;slid=mr3&slau=display&ptgt=p&w=300&h=250&flag=+cmpn`;
    }

    return adTagUrl;
  };

  const updateAdTagUrl = (updatedVideoId) => {
    s2nPlayer.current = document.querySelector('script[data-type="s2nScript"]');

    // Set disable_ads to true if there is no ad_tag_url set
    if (! ad_tag_url && ! isLiveStream(videoId)) {
      return setAds({
        disable_ads: true,
        ad_tag_url: undefined,
      });
    }

    const processAdTag = function (adTagUrl, params) {
      const customParams = new URLSearchParams(prepareCustParams(videoId.toString(), true) || {});
      const permutiveParams = customParams.get('permutive');
      extraParams.current = `ads.dfp_params=${encodeURIComponent(customParams).toString()}${permutiveParams ? `&ads.permutive=${permutiveParams}` : ''}&ads.dfp_pal_nonce=[NONCE]&ads.dfp_ppid=${ppid}`;
      const freeWheelParams = prepareFreewheelParams(videoId, isPermutiveEnabled);

      if (isLiveStream(videoId)) {
        const liveStreamParams = addLivestreamFreewheelParams(ppid, freeWheelParams);
        extraParams.current += `${liveStreamParams}`;
      }

      if (isFreewheelAds) {
        freeWheelParams.isAmp = isAmp;
        freeWheelParams.autoplay = autoplay;
        freeWheelParams.ssid = getCookie('SpringServe_ssid');
        freeWheelParams.volume_level = playerParams.volume;
        if (updatedVideoId) freeWheelParams.videoId = updatedVideoId;
        adTagUrl = updateFreewheelAdTagUrlWithBids(updateFreewheelUrl(adTagUrl, freeWheelParams), params); // eslint-disable-line no-param-reassign
        adTagUrl = updateAdTagWithCompanionAd(adTagUrl); // eslint-disable-line no-param-reassign
        adTagUrl = applyVideoClassificationToFreewheelAdTagUrl(adTagUrl, { // eslint-disable-line no-param-reassign
          videoClassifications,
          playerName,
          videoId,
          isLeadMedia: parseInt(isLeadMedia, 10),
          autoplay: parseInt(autoplay, 10),
          isPost: parseInt(isPost, 10),
        });

        if (!isLiveStream(videoId) && ppid) {
          extraParams.current += `&ads.dfp_ppid=${ppid}`;
        }

        return adTagUrl;
      }

      // For GAM only
      adTagUrl = applyVideoClassificationToAdTagUrl(adTagUrl, { // eslint-disable-line no-param-reassign
        videoClassifications,
        playerName,
        videoId,
        isLeadMedia: parseInt(isLeadMedia, 10),
        autoplay: parseInt(autoplay, 10),
        isPost: parseInt(isPost, 10),
      });

      // Add autoplay KVP directly to the GAM ad tag URL
      try {
        const url = new URL(adTagUrl);
        url.searchParams.set('autoplay', autoplay ? 'true' : 'false');
        adTagUrl = url.toString(); // eslint-disable-line no-param-reassign
      } catch (error) {
        console.error('Failed to construct URL or set autoplay parameter:', error);
      }
      return prepareAdTagUrl(videoId, adTagUrl, params, isPermutiveEnabled);
    };

    return refreshBids(slotID).then((values) => {
      // Gather values from refreshBids function
      const params = values.length ? values.reduce((prev, next) => ({ ...prev, ...next })) : {};
      const hasAds = !! Object.keys(ads).length;
      setAds({
        disable_ads: ! ad_tag_url,
        ad_tag_url: processAdTag(hasAds ? ads.ad_tag_url : ad_tag_url, params),
        expectPrerollTimeout,
        isFreewheel: isFreewheelAds,
      });
    }).catch(() => {
      // Dont change the ads value if the ads already set, call prepareAdTagUrl at initial setup with empty bids object otherwise.
      // Always update frewheel ad tag url.
      if (Object.keys(ads).length && ! isFreewheelAds) return;
      setAds({
        disable_ads: ! ad_tag_url,
        ad_tag_url: processAdTag(ad_tag_url, {}),
        expectPrerollTimeout,
        isFreewheel: isFreewheelAds,
      });
    });
  };
  const updateAdTagUrlWithTracker = () => {
    const stepTracker = new SimpleErrorTracker('Update ad tag url', 100, trackParams);
    try {
      updateAdTagUrl();
      stepTracker.sendSuccess();
    } catch (err) {
      stepTracker.sendError(err, 10);
    }
  };

  const setRenderPlayerAndTrack = () => {
    const stepTracker = new SimpleErrorTracker('Set render player to true', 100, trackParams);
    setRenderPlayer(true);
    stepTracker.sendSuccess();
  };


  const updateHeroTitle = (newTitle) => {
    const container = document.querySelector(`[data-player_id="${player_id}"]`);
    if (! container) return;
    const parent = container?.parentElement?.parentElement;
    if (! parent) return;

    if ('video-hero' === parent.id || 'video-hero-right-rail' === parent.id) {
      const heroTitle = parent.querySelector('.article-list__title');
      if (! heroTitle) return;

      heroTitle.innerText = newTitle;
    }
  };

  const handleOVPEvents = (event) => {
    const { eventName, playerId, args } = event.detail || {};
    if (player_id !== playerId) return;

    if ('titleUpdated' === eventName) {
      updateHeroTitle(args?.videoTitle);
    }
  };

  // Call updateAdTagUrl function for the initial client side prebid and a9 updates
  useEffect(() => {
    if (! Array.isArray(videoId)) {
      updateShareEmbed(videoId);
    }

    if (parseInt(forceRender, 10) || parseInt(isLeadMedia, 10)) {
      updateAdTagUrlWithTracker();
      return;
    }

    triggerIfVisible(`[data-player_id="${player_id}"]`, 'video-placeholder', () => {
      updateAdTagUrlWithTracker();
    });

    window.addEventListener('OVPPlayerEvent', handleOVPEvents);
  }, []);

  useEffect(() => {
    if (0 < Object.keys(ads).length) setRenderPlayerAndTrack();
  }, [ads]);

  const updatePlayerTitle = (payload) => {
    const isFeed = 'feed' === videoId?.type;
    const identifier = videoId.toString();
    const customId = isFeed ? `${videoId?.id}` : identifier;
    const videoPlayer = videojs.getPlayer(document.querySelector(`[data-internal-video-id="${customId}"]`));
    const playlistIndex = videoPlayer?.playlist()?.current_index();
    // First option grabs the video title if it's a playlist video.
    // Second grabs the video title if it's from recommendations.
    // Except the second also works if the user goes back into the playlist using the UI.
    const newTitle = payload?.videoInfo?.[playlistIndex]?.title || payload?.videoInfo?.title;

    if (! videoPlayer) return;

    updateHeroTitle(newTitle);
  };

  // Changes to apply for each video of a playlist
  const handleStart = (videoPayload) => {
    updatePlayerTitle(videoPayload);
    // Expose the player so it can be use by other plugins.
    const videoPlayer = videojs.getPlayer(document.querySelector(`[data-internal-video-id="${videoId?.id || videoId}"]`));
    if (! videoPlayer) return;
    if (! window?.ovpPlayers[player_id]) {
      window.ovpPlayers[player_id] = videoPlayer;
      customOvpEvent(videoId, videoPayload, 'VIDEO_INIT');
    }

    if ((! Array.isArray(videoId) && 'feed' !== videoId?.type)) return;

    const playlistUpcomingIndex = videoPlayer?.playlist()?.current_index() + 1;
    const newVideoId = videoPayload?.videoInfo?.[playlistUpcomingIndex]?.id;
    updateShareEmbed(newVideoId);

    if (! ad_tag_url) return;
    const updatedVideoId = isFreewheelAds ? newVideoId : undefined;
    updateAdTagUrl(updatedVideoId);
  };

  const handleEnd = (videoPayload) => {
    customOvpEvent(videoId, videoPayload, 'VIDEO_COMPLETED');
  };

  const handleUpdateAds = (updatedAds, updatedVideoId) => {
    const adTagUrl = isFreewheelAds
      ? updateFreewheelUrl(updatedAds?.ad_tag_url, {
        ...prepareFreewheelParams(videoId, isPermutiveEnabled),
        videoId: updatedVideoId,
        isAmp,
        autoplay,
        volume_level: playerParams.volume
      })
      : updatedAds?.ad_tag_url;

    refreshBids(slotID).then((values) => {
      // Gather values from refreshBids function
      const params = values.length ? values.reduce((prev, next) => ({ ...prev, ...next })) : {};
      // Update bids by calling prepareBids
      setAds({
        disable_ads: ! ad_tag_url,
        ad_tag_url: prepareBids(adTagUrl, params),
        expectPrerollTimeout,
        isFreewheel: isFreewheelAds,
      });
    }).catch(() => {
      if (! isFreewheelAds) return;

      setAds({
        disable_ads: ! ad_tag_url,
        ad_tag_url: adTagUrl,
        expectPrerollTimeout,
        isFreewheel: isFreewheelAds,
      });
    });
  };

  console.log('***', initiallySticky);

  return (
    (renderPlayer && (
      <VideoPlayerComponent
        {...playerParams} // eslint-disable-line react/jsx-props-no-spreading
        video_id={videoId}
        ads={ads}
        autoplay={autoplay || false}
		is_manually_triggered={! autoplay}
        on_start={handleStart}
        on_end={handleEnd}
        sticky={initiallySticky || (isEmbedVideoInPost && ! isNewsNationTheme)}
        share_embed={shareEmbed}
        min_player_enabled={minimizeEnabled}
        _udl={window.NXSTdata}
        bobBaseHost={bobBaseHost}
        update_ads={handleUpdateAds}
        show_title_overlay={!! (titleVisible && (
          isNewsNationTheme
          || isEmbedVideoInPost
          || parseInt(isLeadMedia, 10)
          || (initiallySticky && ! s2nPlayer.current)
        ))}
        overrided_title={title}
        extra_params={extraParams.current}
        volume_level={playerParams.volume}
        recommendations={recommendations?.items || (isEmbedVideoInPost || recommendations)}
        player_name={playerName}
        player_id={player_id}
        anvack={anvack}
        nielsen={nielsen}
        comscore_enabled={isComscoreEnabled}
      />
    ))
  );
};

VideoPlayer.propTypes = {
  video_id: PropTypes.string.isRequired,
  post_id: PropTypes.number,
  playerParams: PropTypes.shape({
    autoplay: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    recommendations: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    isPermutiveEnabled: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    expectPrerollTimeout: PropTypes.number,
    titleVisible: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    htmlEmbed: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    plugins: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    video: PropTypes.string,
    playlist: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    volume: PropTypes.number,
    isAmp: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    videoClassification: PropTypes.arrayOf(PropTypes.shape({
      playerLocation: PropTypes.string,
      platform: PropTypes.string,
      placement: PropTypes.number,
      plcmt: PropTypes.number,
      playbackmethod: PropTypes.number,
    })),
  }).isRequired,
  playerName: PropTypes.string.isRequired,
  injected_via: PropTypes.string,
  allow_render: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  sticky: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  minimized: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  bob_pixel_url: PropTypes.string,
  is_lead_media: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  is_post: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  player_name: PropTypes.string,
  video_classifications: PropTypes.arrayOf(PropTypes.shape({
    playerLocation: PropTypes.string,
    platform: PropTypes.string,
    placement: PropTypes.number,
    plcmt: PropTypes.number,
    playbackmethod: PropTypes.number,
  })),
  tracker: PropTypes.oneOfType([SimpleErrorTracker]).isRequired,
  player_id: PropTypes.number.isRequired,
};

VideoPlayer.defaultProps = {
  allow_render: 0,
  post_id: 0,
  sticky: 0,
  minimized: 0,
  bob_pixel_url: '',
  is_lead_media: 0,
  is_post: 0,
  player_name: '',
  injected_via: '',
  video_classifications: [],
};

export default VideoPlayer;
